import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';

/**
 * Hook to fetch recipe images from Supabase storage
 * @param {string} recipeId - The ID of the recipe
 * @returns {Promise<string|null>} - Returns the image URL or null if not found
 */
const useSupabaseRecipeImage = (recipeId, shouldFetch = false, refreshTrigger = 0) => {
    const [imageUrl, setImageUrl] = useState(null);
    const { sessionToken } = useAuth();

    useEffect(() => {
        let blobUrl = null;
        const fetchSupabaseImage = async () => {
            if (!recipeId || !sessionToken || !shouldFetch) return;

            try {
                const response = await fetchWithCSRF(`/api/v1/supabase/recipe-images/recipe/${recipeId}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });
                
                if (!response.ok) {
                    // Silently fail for 403/404 - images might not exist in Supabase
                    if (response.status === 403 || response.status === 404) {
                        return;
                    }
                    console.log('Supabase image not found:', recipeId);
                    return;
                }

                // Get the image data as a blob
                const imageBlob = await response.blob();
                // Create a blob URL that can be used directly in an img tag
                blobUrl = URL.createObjectURL(imageBlob);
                setImageUrl(blobUrl);
            } catch (err) {
                // Silently fail - will use fallback image
                return null;
            }
        };

        if (shouldFetch) {
            fetchSupabaseImage();
        } else {
            setImageUrl(null); // Reset URL when we're not fetching
        }

        // Cleanup function to revoke the blob URL when component unmounts or URL changes
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [recipeId, sessionToken, shouldFetch, refreshTrigger]); // Added shouldFetch to dependencies

    return imageUrl;
};

// Hook for uploading a new recipe image
const useAddRecipeImage = () => {
    const { sessionToken } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);

    const uploadImage = async (recipeId, imageFile) => {
        if (!sessionToken || !recipeId || !imageFile) return;
        setIsUploading(true);
        setError(null);

        try {
            // Create FormData for the file upload
            const formData = new FormData();
            formData.append('file', imageFile);

            console.log("sending delete request with sessionToken: ", sessionToken)

            // First, try to delete existing image if it exists
            try {
                const deleteResponse = await fetchWithCSRF(`/api/v1/supabase/recipe-images/recipe/${recipeId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });
                if (!deleteResponse.ok) {
                    console.log('Delete response not OK:', await deleteResponse.text());
                }
            } catch (deleteError) {
                console.log('No existing image to delete or delete failed:', deleteError);
            }

            // Upload the new image
            const response = await fetchWithCSRF(`/api/v1/supabase/recipe-images/recipe/${recipeId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`,
                    // Let the browser set the Content-Type for FormData
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
            }

            return true;
        } catch (err) {
            console.error('Error uploading image:', err);
            setError(err.message);
            return false;
        } finally {
            setIsUploading(false);
        }
    };

    return { uploadImage, isUploading, error };
};

export { useSupabaseRecipeImage, useAddRecipeImage };
